// src/utils/toastSystem.ts
// Universal Toast Notification System for TestWerk.ai

export interface ToastOptions {
  type: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  message: string;
  duration?: number; // milliseconds
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  persistent?: boolean; // Stay until manually closed
  showProgress?: boolean; // Show countdown progress bar
}

class ToastManager {
  private toasts: Map<string, HTMLElement> = new Map();
  private container: HTMLElement | null = null;

  constructor() {
    this.createContainer();
  }

  private createContainer() {
    // Remove existing container if it exists
    const existing = document.getElementById('testwerk-toast-container');
    if (existing) {
      existing.remove();
    }

    this.container = document.createElement('div');
    this.container.id = 'testwerk-toast-container';
    this.container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      pointer-events: none;
      max-width: 400px;
    `;
    document.body.appendChild(this.container);
  }

  show(options: ToastOptions): string {
    const toastId = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const duration = options.duration || (options.persistent ? 0 : 10000); // 10 seconds default

    const toast = this.createToastElement(toastId, options, duration);
    
    if (!this.container) {
      this.createContainer();
    }
    
    this.container!.appendChild(toast);
    this.toasts.set(toastId, toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
      toast.style.opacity = '1';
    }, 100);

    // Auto-remove if not persistent
    if (!options.persistent && duration > 0) {
      setTimeout(() => {
        this.remove(toastId);
      }, duration);
    }

    return toastId;
  }

  private createToastElement(toastId: string, options: ToastOptions, duration: number): HTMLElement {
    const toast = document.createElement('div');
    toast.id = toastId;
    
    const colors = {
      success: { bg: '#28a745', border: '#1e7e34', icon: '✅' },
      error: { bg: '#dc3545', border: '#bd2130', icon: '❌' },
      info: { bg: '#17a2b8', border: '#138496', icon: 'ℹ️' },
      warning: { bg: '#ffc107', border: '#e0a800', icon: '⚠️', textColor: '#212529' }
    };

    const color = colors[options.type];
    const textColor = color.textColor || '#ffffff';

    toast.style.cssText = `
      background: linear-gradient(135deg, ${color.bg}, ${color.border});
      color: ${textColor};
      padding: 1rem 1.5rem;
      border-radius: 12px;
      margin-bottom: 1rem;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      border: 1px solid ${color.border};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
      max-width: 100%;
      word-wrap: break-word;
      pointer-events: auto;
      cursor: pointer;
      transform: translateX(100%);
      opacity: 0;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
    `;

    // Create content
    const content = document.createElement('div');
    content.style.cssText = `
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    `;

    // Icon
    const icon = document.createElement('div');
    icon.textContent = color.icon;
    icon.style.cssText = `
      font-size: 18px;
      flex-shrink: 0;
      margin-top: 2px;
    `;

    // Message content
    const messageContent = document.createElement('div');
    messageContent.style.cssText = 'flex: 1;';

    if (options.title) {
      const title = document.createElement('div');
      title.textContent = options.title;
      title.style.cssText = `
        font-weight: bold;
        font-size: 15px;
        margin-bottom: 4px;
      `;
      messageContent.appendChild(title);
    }

    const message = document.createElement('div');
    message.textContent = options.message;
    message.style.cssText = `
      opacity: 0.95;
      white-space: pre-line;
    `;
    messageContent.appendChild(message);

    // Close button
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.style.cssText = `
      background: none;
      border: none;
      color: ${textColor};
      font-size: 20px;
      font-weight: bold;
      cursor: pointer;
      opacity: 0.7;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;
      flex-shrink: 0;
    `;

    closeButton.addEventListener('mouseenter', () => {
      closeButton.style.opacity = '1';
      closeButton.style.backgroundColor = 'rgba(255,255,255,0.2)';
    });

    closeButton.addEventListener('mouseleave', () => {
      closeButton.style.opacity = '0.7';
      closeButton.style.backgroundColor = 'transparent';
    });

    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.remove(toastId);
    });

    // Progress bar for timed toasts
    if (options.showProgress && duration > 0 && !options.persistent) {
      const progressBar = document.createElement('div');
      progressBar.style.cssText = `
        position: absolute;
        bottom: 0;
        left: 0;
        height: 3px;
        background: rgba(255,255,255,0.3);
        width: 100%;
        transform-origin: left;
        animation: toast-progress ${duration}ms linear forwards;
      `;

      // Add CSS animation
      if (!document.getElementById('toast-progress-styles')) {
        const styles = document.createElement('style');
        styles.id = 'toast-progress-styles';
        styles.textContent = `
          @keyframes toast-progress {
            from { transform: scaleX(1); }
            to { transform: scaleX(0); }
          }
        `;
        document.head.appendChild(styles);
      }

      toast.appendChild(progressBar);
    }

    // Assemble toast
    content.appendChild(icon);
    content.appendChild(messageContent);
    content.appendChild(closeButton);
    toast.appendChild(content);

    // Click to dismiss
    toast.addEventListener('click', () => {
      this.remove(toastId);
    });

    return toast;
  }

  remove(toastId: string) {
    const toast = this.toasts.get(toastId);
    if (toast) {
      toast.style.transform = 'translateX(100%)';
      toast.style.opacity = '0';
      
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
        this.toasts.delete(toastId);
      }, 300);
    }
  }

  removeAll() {
    this.toasts.forEach((_, toastId) => {
      this.remove(toastId);
    });
  }
}

// Global instance
const toastManager = new ToastManager();

// Convenience functions
export const showToast = (options: ToastOptions): string => {
  return toastManager.show(options);
};

export const showSuccess = (message: string, title?: string, duration = 10000): string => {
  return showToast({
    type: 'success',
    title,
    message,
    duration,
    showProgress: true
  });
};

export const showError = (message: string, title?: string, persistent = true): string => {
  return showToast({
    type: 'error',
    title,
    message,
    persistent,
    showProgress: false
  });
};

export const showInfo = (message: string, title?: string, duration = 8000): string => {
  return showToast({
    type: 'info',
    title,
    message,
    duration,
    showProgress: true
  });
};

export const showWarning = (message: string, title?: string, duration = 12000): string => {
  return showToast({
    type: 'warning',
    title,
    message,
    duration,
    showProgress: true
  });
};

// TestWerk.ai specific notifications
export const TestWerkToasts = {
  agentUploadComplete: (agentName: string) => {
    return showSuccess(
      `Agent "${agentName}" is now ready for testing!\nAll dependencies installed successfully.`,
      'Agent Upload Complete',
      12000
    );
  },

  testGenerationComplete: (count: number, agentName: string) => {
    return showSuccess(
      `Generated ${count} intelligent test cases for "${agentName}".\nReady for execution!`,
      'Test Generation Complete',
      10000
    );
  },

  testExecutionComplete: (passed: number, failed: number, agentName: string) => {
    const total = passed + failed;
    const success = failed === 0;
    
    return (success ? showSuccess : showWarning)(
      `Executed ${total} tests for "${agentName}":\n✅ ${passed} passed${failed > 0 ? `\n❌ ${failed} failed` : ''}`,
      success ? 'Test Execution Complete' : '⚠️ Test Execution Complete',
      success ? 10000 : 15000
    );
  },

  agentUploadError: (error: string) => {
    return showError(
      `Upload failed: ${error}\nPlease check your agent package and try again.`,
      '❌ Agent Upload Failed'
    );
  },

  testExecutionError: (testName: string, error: string) => {
    return showError(
      `Test "${testName}" failed to execute:\n${error}`,
      '❌ Test Execution Error'
    );
  },

  systemHealthWarning: (unhealthyServices: string[]) => {
    return showWarning(
      `Some services are unhealthy:\n${unhealthyServices.join(', ')}\nSystem functionality may be limited.`,
      '⚠️ System Health Warning',
      15000
    );
  },

  aiConfigUpdated: (model: string) => {
    return showInfo(
      `ACTIVE :: ${model}`,
      'AI CONFIGURATION',
      8000
    );
  }
};

export default toastManager;